import { createI18n, getActiveI18n, t } from "@magic-md/i18n";
import { useI18n, useT } from "@magic-md/i18n/react";
import { useMemo } from "react";
//#region src/i18n.ts
const EXTENSION_SDK_NAMESPACE = "extensionSdk";
function useExtensionSdkT() {
	return useT(EXTENSION_SDK_NAMESPACE);
}
function extensionSdkT(key, params) {
	return t(EXTENSION_SDK_NAMESPACE, key, params);
}
/**
* Hook returning a scoped translator for an extension's own locale bundles.
* Mirrors plugin-sdk `useMagicMdT` — re-renders on active locale change.
*/
function useMagicMdT(namespace, bundles) {
	const active = useI18n();
	return useMemo(() => {
		const i18n = createI18n({
			requestedLocale: active.locale,
			fallbackLocale: active.fallbackLocale,
			bundles
		});
		return (key, params) => i18n.t(namespace, key, params);
	}, [
		active.fallbackLocale,
		active.locale,
		bundles,
		namespace
	]);
}
function magicMdT(namespace, key, params = {}, bundles) {
	const active = getActiveI18n();
	return createI18n({
		requestedLocale: active.locale,
		fallbackLocale: active.fallbackLocale,
		bundles
	}).t(namespace, key, params);
}
//#endregion
//#region src/manifest-i18n.ts
const LOCAL_STATIC_MESSAGE_REF_RE = /^%([^%\s]+)%$/;
/**
* Resolve a `%key%` reference against an extension's i18n bundles. Plain strings
* pass through unchanged. Mirrors plugin-sdk `resolvePluginText`.
*/
function resolveExtensionText(value, i18n, options = {}) {
	if (value === void 0 || !i18n) return value;
	const match = LOCAL_STATIC_MESSAGE_REF_RE.exec(value.trim());
	if (!match) return value;
	const active = getActiveI18n();
	const translated = createI18n({
		requestedLocale: options.locale ?? active.locale,
		fallbackLocale: options.fallbackLocale ?? active.fallbackLocale,
		bundles: i18n.bundles
	}).t(i18n.namespace, match[1], options.params);
	return translated === match[1] ? value : translated;
}
function resolveExtensionValue(value, i18n, options = {}) {
	if (!i18n) return value;
	if (typeof value === "string") return resolveExtensionText(value, i18n, options);
	if (Array.isArray(value)) return value.map((item) => resolveExtensionValue(item, i18n, options));
	if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, resolveExtensionValue(item, i18n, options)]));
	return value;
}
function resolveExtensionManifest(manifest, i18n, options = {}) {
	return resolveExtensionValue(manifest, i18n, options);
}
//#endregion
//#region src/utils/defineExtension.ts
/**
* Define a full extension entry (UI + Runtime). Mirrors `definePlugin`.
*/
function defineExtension(extension) {
	return extension;
}
/**
* Define the UI layer (browser). Contributions are registered in `activate(ctx)`.
* May import React / lucide / plugin-sdk UI. Must NOT call sidecars directly.
*/
function defineExtensionUI(ui) {
	return ui;
}
/**
* Define the runtime layer (local Node / cloud Worker). Hosts long-lived logic
* and sidecar calls. Must NOT import React.
*/
function defineExtensionRuntime(runtime) {
	return runtime;
}
//#endregion
export { defineExtension, defineExtensionRuntime, defineExtensionUI, extensionSdkT, magicMdT, resolveExtensionManifest, resolveExtensionText, resolveExtensionValue, useExtensionSdkT, useMagicMdT };
